<template>
  <div
    ref="el"
    class="w-full min-w-0 max-w-full"
  >
    <template v-if="contactType === 'external'">
      <call-item-external
        class="w-full min-w-0"
        :number="externalNumber"
        :icon-name="iconName"
        :icon-color="iconColor"
        :date="callDate"
        :duration="duration"
        :call-in-progress="!!activeExternalSession"
        :event-item="(messageItem as ExternalCall)"
      />
    </template>
    <template v-if="contactType === 'anonymous'">
      <call-item-external
        class="w-full min-w-0"
        number="Номер скрыт"
        :icon-name="iconName"
        :icon-color="iconColor"
        :date="callDate"
        :duration="duration"
        :call-in-progress="!!activeExternalSession"
        :event-item="(messageItem as ExternalCall)"
      />
    </template>
    <template v-else-if="contactType === 'room'">
      <div
        :key="roomId"
        class="w-full min-w-0"
        @click="handleItemClick"
      >
        <call-item-room
          class="w-full min-w-0"
          :item="messageItem"
          :room-id="roomId"
          :icon-name="iconName"
          :icon-color="iconColor"
          :date="callDate"
          :is-selected="selectedEventId === messageItem.eventId"
          :live-call-sessions="liveCallSessions"
          @show-context="onShowContext"
        />
      </div>
    </template>
    <template v-else-if="contactType === 'contact' && contact">
      <div
        :key="contact.id"
        class="w-full min-w-0"
        @click="handleItemClick"
      >
        <call-item-contact
          class="w-full min-w-0"
          :contact="contact"
          :event-item="messageItem"
          :icon-name="iconName"
          :icon-color="iconColor"
          :duration="duration"
          :date="callDate"
          :call-in-progress="!!activeContactSession"
          :is-selected="selectedEventId === messageItem.eventId"
          @show-context="onShowContext"
        />
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import type { UnlistenFn } from '@tauri-apps/api/event'
import { IconName, useDialog } from '@wui/common-library'
import { HistoryMessageItem } from '@wui/im'
import { computed, ref, onUpdated, onMounted, onUnmounted, watch } from 'vue'

import { useChatEvent } from '@/entities/chat-handlers'
import { useStoreContacts } from '@/entities/contact'
import { useStoreRooms } from '@/entities/room'


import { DeletedConferenceDialog } from '@/shared/components/dialogs'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { isWindowHidden, onWindowVisibilityChange } from '@/shared/services/window-visibility'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { CALL_CONTACT_URI } from '@/shared/utils/constants'
import { conversionFromNumberToTime, convertTimestampToDateTimeString } from '@/shared/utils/date-time'
import { optionalPush } from '@/shared/utils/router'
import { fromPServedUser, fromPServedUserIfRoom, pServedIsRoom } from '@/shared/utils/uri'
import { getChatRoute } from '@/shared/utils/url'

import {
  findLiveCallSession,
  getCallForwardedFromLabel,
  getCallHistoryEventId,
  getCallHistoryPServed,
  getCallHistoryRank,
  getDeletedConferenceName,
  hasCallSourceLink,
  isCallForwardedEvent,
  isDeletedConferenceCall,
  resolveCallHistoryContactPServed,
} from '../lib'
import type { CallEventWithStatus, ContactType, ExternalCall } from '../types'
import { useLiveCallSessions } from '../use-live-call-sessions'

import CallItemContact from './CallItemContact.vue'
import CallItemExternal from './CallItemExternal.vue'
import CallItemRoom from './CallItemRoom.vue'

const props = defineProps<{
  item: CallEventWithStatus
  next?: CallEventWithStatus
  isVisible?: boolean
}>()

const emit = defineEmits<{
  (event: 'show-context', e: MouseEvent | TouchEvent, room: Room | Contact): void
}>()

const historyMessageItem = computed<HistoryMessageItem<CallEventWithStatus>>(() => ({
  eventId: getCallHistoryEventId(props.item),
  item: {
    ...props.item,
    pServed: getCallHistoryPServed(props.item),
  },
  rank: getCallHistoryRank(props.item),
  status: props.item.status,
  timestamp: props.item.timestamp,
  type: 0,
}))

const { currentUser } = useCurrentUser()
const storeContacts = useStoreContacts()

const messageItem = computed(() => historyMessageItem.value.item)
const contact = computed(() => {
  const pServed = resolveCallHistoryContactPServed(messageItem.value, currentUser.value?.imLogin)
  const found = storeContacts.findByPServed(pServed ?? messageItem.value.pServed)
  if (found) return found
  if (!isCallForwardedEvent(messageItem.value) || !pServed) return undefined
  const number = fromPServedUser(pServed)
  return number ? storeContacts.findByInternalNumber(number) : undefined
})
const nextHistoryMessageItem = computed<HistoryMessageItem<CallEventWithStatus> | undefined>(() => {
  if (!props.next) {
    return undefined
  }
  return {
    eventId: getCallHistoryEventId(props.next),
    item: {
      ...props.next,
      pServed: getCallHistoryPServed(props.next),
    },
    rank: getCallHistoryRank(props.next),
    status: props.next.status,
    timestamp: props.next.timestamp,
    type: 0,
  }
})

const room = computed(() => storeRooms.findByPServed(historyMessageItem.value.item.pServed))

const { liveCallSessions } = useLiveCallSessions()
const storeRooms = useStoreRooms()
const { showDialog } = useDialog()
const isCallHistoryReadLocked = computed(() => !hasCallSourceLink(props.item))
const { el, observer } = useChatEvent<CallEventWithStatus>(
  historyMessageItem,
  nextHistoryMessageItem,
  undefined,
  true,
  isCallHistoryReadLocked,
)

const selectedEventId = ref<number>(0)

const contactType = computed<ContactType>(() => {
  if (room.value?.isGroup || pServedIsRoom(historyMessageItem.value.item.pServed) || ('roomName' in props.item && !!props.item.roomName)) {
    return 'room'
  } else if (contact.value) {
    return 'contact'
  } else if (props.item.from === 'anonymous') {
    return 'anonymous'
  } else {
    return 'external'
  }
})

const externalNumber = computed(() => {
  if (isCallForwardedEvent(props.item)) {
    const peer = resolveCallHistoryContactPServed(props.item, currentUser.value?.imLogin)
    if (peer) return fromPServedUser(peer) || peer
    const from = getCallForwardedFromLabel(props.item, currentUser.value?.imLogin)
    return from || fromPServedUser(props.item.callForwardDestination || '')
  }

  let from = props.item.from
  if ((!from || from === CALL_CONTACT_URI) && 'srcNumber' in props.item) {
    from = props.item.srcNumber
  }

  let to = props.item.to
  if ((!to || to === CALL_CONTACT_URI) && 'dstNumber' in props.item) {
    to = props.item.dstNumber
  }

  if (from === currentUser.value?.imLogin) {
    return fromPServedUser(to)
  }

  if (to === currentUser.value?.imLogin) {
    return fromPServedUser(from)
  }

  return fromPServedUser(from ?? to)
})

const isOutgoing = computed(() => {
  return props.item.srcPServed === currentUser.value?.imLogin
})

const iconColor = computed(() => {
  if (props.item.event === 'call_missed') {
    return 'text-warning'
  }
  if (props.item.event === 'dialing_rejected' || props.item.event === 'dialing_terminated' || props.item.event === 'dialing_busy') {
    return 'text-warning'
  }
  if (props.item.event === 'appointment_end') {
    return 'text-positive'
  }
  if (props.item.event === 'call_forwarded') {
    return 'text-threshold'
  }
  return 'text-positive'
})

const iconName = computed<IconName>(() => {
  switch (props.item.event) {
  case 'call_missed': {
    return 'arrowElbowLeftM'
  }
  case 'dialing_rejected':
  case 'dialing_terminated':
  case 'dialing_busy': {
    return isOutgoing.value ? 'arrowNorthEastM' : 'arrowSouthWestM'
  }
  case 'appointment_end': {
    return 'arrowSouthWestM'
  }
  case 'call_forwarded': {
    return 'arrowRedoM'
  }
  default: {
    if ('dstPServed' in props.item && currentUser.value?.imLogin === props.item.dstPServed) {
      return 'arrowSouthWestM'
    } else {
      return 'arrowNorthEastM'
    }
  }
  }
})

const { t } = useLocalization()

const callDate = computed(() => {
  return convertTimestampToDateTimeString(props.item.timestamp)
})

const duration = computed(() => {
  if (props.item.event === 'call_forwarded' && 'callForwardDestination' in props.item) {
    const reason = t('common.callForwardReason.' + props.item.callForwardReason)
    return reason
  }
  return conversionFromNumberToTime('duration' in props.item ? props.item.duration : 0)
})

const activeContactSession = computed(() => {
  if (!contact.value) {
    return undefined
  }

  return findLiveCallSession(liveCallSessions.value, { contact: contact.value })
})

const activeExternalSession = computed(() => findLiveCallSession(liveCallSessions.value, { number: externalNumber.value }))

const roomId = computed(() => {
  /**
   * TODO move to IM library
   */
  if (isCallForwardedEvent(props.item)) {
    const peer = resolveCallHistoryContactPServed(props.item, currentUser.value?.imLogin)
    return fromPServedUserIfRoom(peer ?? historyMessageItem.value.item.pServed)
  }
  const { dstPServed, srcPServed } = props.item
  let pServed = historyMessageItem.value.item.pServed
  if (srcPServed && dstPServed === currentUser.value?.imLogin) {
    pServed = srcPServed
  } else if (dstPServed && srcPServed === currentUser.value?.imLogin) {
    pServed = dstPServed
  }
  return fromPServedUserIfRoom(pServed)
})

const hasChatRoute = computed(() => !!getChatRoute(contact.value ?? roomId.value))

const deletedConferenceName = computed(() => getDeletedConferenceName(messageItem.value, roomId.value))

const resetObservable = () => {
  if (el.value) {
    observer.unobserve(el.value)
    observer.observe(el.value)
  }
}

const onShowContext = (event: MouseEvent | TouchEvent, item: Contact | Room) => {
  emit('show-context', event, item)
}

const handleItemClick = () => {
  if (isDeletedConferenceCall(contactType.value, room.value)) {
    showDialog(DeletedConferenceDialog, {
      hasOverlay: true,
      roomName: deletedConferenceName.value,
    })
    return
  }

  if (!hasChatRoute.value) {
    return
  }

  const routeTarget = contact.value ?? roomId.value
  const routeOptions = hasCallSourceLink(props.item)
    ? { hash: `${getCallHistoryEventId(props.item)}` }
    : undefined
  optionalPush(getChatRoute(routeTarget, routeOptions))
}

const focused = ref(true)

isWindowHidden().then((hidden) => {
  focused.value = !hidden
})

onUpdated(() => {
  if (focused.value) {
    resetObservable()
  }
})

onMounted(() => {
  if (focused.value) {
    resetObservable()
  }
})

const { subscribe, unsubscribe } = useStatusSubscribe()

onMounted(() => {
  subscribe(historyMessageItem.value.item.pServed)
})

onUnmounted(() => {
  unsubscribe(historyMessageItem.value.item.pServed)
})

watch(() => props.isVisible, () => {
  if (props.isVisible) {
    subscribe(historyMessageItem.value.item.pServed)
  } else {
    unsubscribe(historyMessageItem.value.item.pServed)
  }
})

const unListenFn = ref<UnlistenFn>(() => ({}))

onWindowVisibilityChange((event: boolean) => {
  focused.value = event
  if (event) {
    resetObservable()
  }
}).then((r) => {
  unListenFn.value = r
})

onUnmounted(unListenFn.value)
</script>
